import { useQueries } from '@tanstack/preact-query';
import type { ApiClient } from '../../api.js';
import type { AnalyticsSnapshot, ContentCode, SubmissionRecord } from '../../types.js';

export const workspaceQueryKeys = {
  root: ['workspace'] as const,
  codes: (workspaceId: string) => [...workspaceQueryKeys.root, workspaceId, 'codes'] as const,
  analytics: (workspaceId: string) => [...workspaceQueryKeys.root, workspaceId, 'analytics'] as const,
  submissions: (workspaceId: string, filters: { limit: number; incidentState?: 'open' }) => [
    ...workspaceQueryKeys.root,
    workspaceId,
    'submissions',
    filters,
  ] as const,
};

export function useWorkspaceHomeQueries(api: ApiClient, workspaceId: string) {
  const [codes, analytics, records, openIncidents] = useQueries({
    queries: [
      {
        queryKey: workspaceQueryKeys.codes(workspaceId),
        queryFn: () => api.request<{ codes: ContentCode[] }>(`/api/v1/workspaces/${workspaceId}/codes`),
      },
      {
        queryKey: workspaceQueryKeys.analytics(workspaceId),
        queryFn: () => api.request<{ analytics: AnalyticsSnapshot }>(`/api/v1/workspaces/${workspaceId}/analytics`),
      },
      {
        queryKey: workspaceQueryKeys.submissions(workspaceId, { limit: 3 }),
        queryFn: () => api.request<{ submissions: SubmissionRecord[] }>(`/api/v1/workspaces/${workspaceId}/submissions?limit=3`),
      },
      {
        queryKey: workspaceQueryKeys.submissions(workspaceId, { limit: 5, incidentState: 'open' }),
        queryFn: () => api.request<{ submissions: SubmissionRecord[] }>(`/api/v1/workspaces/${workspaceId}/submissions?limit=5&incidentState=open`),
      },
    ],
  });

  const error = [codes.error, analytics.error, records.error, openIncidents.error].find(Boolean) ?? null;
  const ready = [codes, analytics, records, openIncidents].every((query) => query.isSuccess);

  return {
    codes: codes.data?.codes ?? null,
    analytics: analytics.data?.analytics ?? null,
    records: records.data?.submissions ?? null,
    openIncidents: openIncidents.data?.submissions ?? null,
    error,
    ready,
    refreshing: ready && [codes, analytics, records, openIncidents].some((query) => query.isFetching),
  };
}
